import { Injectable } from '@angular/core'
import { HttpRequest, HttpHandler, HttpInterceptor } from '@angular/common/http'
import {
  BehaviorSubject,
  Observable,
  catchError,
  filter,
  firstValueFrom,
  from,
  switchMap,
  take,
  tap,
  throwError
} from 'rxjs'
import {
  Logout,
  SetTokenContent,
  USERS_API_URL,
  UsersService
} from '@lla-platform/users/users-data-access'
import { Store } from '@ngxs/store'
import { Router } from '@angular/router'
import { StateResetAll } from 'ngxs-reset-plugin'
import { LOCAL_STORAGE_KEYS } from '@lla-platform/core/core-util'
import { ShowErrorService } from '../services/show-error.service'
import { ToastrService } from 'ngx-toastr'

interface IAllowedUrl {
  url: string
  methoud: 'get' | 'post' | 'put' | 'delete'
}

const REFRESH_LOCK_NAME = 'lla-token-refresh'
const REFRESH_LOCK_TIMEOUT = 15000

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  isRefreshing = false
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null)

  constructor(
    private usersService: UsersService,
    private store: Store,
    private showErrorService: ShowErrorService,
    private router: Router,
    private toastrService: ToastrService
  ) {}

  addAuthHeader(request: HttpRequest<any>) {
    const token = localStorage.getItem(LOCAL_STORAGE_KEYS.JWT_TOKEN)

    if (!token) {
      return request
    }

    const newRequest = request.clone({
      headers: request.headers.set('Authorization', `Bearer ${token}`)
    })

    return newRequest
  }

  logout() {
    this.store.dispatch(new Logout())
    this.router.navigateByUrl('/login')
    this.store.dispatch(new StateResetAll())
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<any> {
    request = request.clone({
      headers: request.headers.set('Client-Page-Url', window?.location?.href?.split('?')[0])
    })
    request = this.addAuthHeader(request)

    // Handle response
    return next.handle(request).pipe(
      catchError((error) => {
        if (request.url.includes(USERS_API_URL.refreshToken)) {
          this.isRefreshing = false
          if (!this.hasNewerToken(request)) {
            this.logout()
          }
          return throwError(() => error)
        }
        if (error.status === 401) {
          const token = localStorage.getItem(LOCAL_STORAGE_KEYS.JWT_TOKEN)
          if (!token && request.url.includes(USERS_API_URL.me)) {
            // Throw error if User not login and there is no token
            this.isRefreshing = false
            return throwError(() => error)
          }
          if (!token) {
            this.isRefreshing = false
            this.logout()
            return throwError(() => error)
          }
          if (!this.isRefreshing) {
            this.isRefreshing = true
            this.refreshTokenSubject.next(null)

            return from(this.refreshSession(token)).pipe(
              tap((newToken) => {
                this.isRefreshing = false
                this.refreshTokenSubject.next(newToken)
              }),
              catchError((refreshError) => {
                this.isRefreshing = false
                return throwError(() => refreshError)
              }),
              switchMap(() => next.handle(this.addAuthHeader(request)))
            )
          }

          // Put the requests in queue
          return this.refreshTokenSubject.pipe(
            filter((token) => token !== null),
            take(1),
            switchMap(() => next.handle(this.addAuthHeader(request)))
          )
        }

        // Failures on these are not surfaced as toasts — they are background requests the user did
        // not initiate, so a transient error should stay silent rather than interrupt them.
        // The USAF account check shows its own failure inline, next to the field.
        const allowedUrl: IAllowedUrl[] = [
          { url: USERS_API_URL.me, methoud: 'get' },
          { url: '/announcements/unread-count', methoud: 'get' },
          { url: '/admin/usaf-account-status', methoud: 'get' }
        ]
        if (
          !allowedUrl.find(
            (el) => request.url.includes(el.url) && request.method.toLowerCase() === el.methoud
          )
        ) {
          this.showErrorService.showHttpError(error)
        }

        if (error?.error?.redirectUrl) {
          window.location.href = error.error.redirectUrl
          this.toastrService.info(
            'You are not logged in to Google account! You will redirect to Google automatically'
          )
        }

        return throwError(() => error)
      })
    )
  }

  private async refreshSession(staleToken: string): Promise<string> {
    const rotate = async () => {
      const currentToken = localStorage.getItem(LOCAL_STORAGE_KEYS.JWT_TOKEN)
      if (currentToken && currentToken !== staleToken) {
        this.store.dispatch(new SetTokenContent())
        return currentToken
      }

      try {
        const res = await firstValueFrom(this.usersService.refreshToken())
        localStorage.setItem(LOCAL_STORAGE_KEYS.JWT_TOKEN, res.token)
        this.store.dispatch(new SetTokenContent())
        return res.token
      } catch (error) {
        const rotatedToken = localStorage.getItem(LOCAL_STORAGE_KEYS.JWT_TOKEN)
        if (rotatedToken && rotatedToken !== staleToken) {
          this.store.dispatch(new SetTokenContent())
          return rotatedToken
        }
        throw error
      }
    }

    const locks = navigator.locks as LockManager | undefined
    if (!locks) {
      return rotate()
    }

    try {
      return await locks.request(
        REFRESH_LOCK_NAME,
        { signal: AbortSignal.timeout(REFRESH_LOCK_TIMEOUT) },
        rotate
      )
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        return rotate()
      }
      throw error
    }
  }

  private hasNewerToken(request: HttpRequest<any>) {
    const attemptedToken = (request.body as { associatedBearerToken?: string })?.associatedBearerToken
    const currentToken = localStorage.getItem(LOCAL_STORAGE_KEYS.JWT_TOKEN)
    return !!currentToken && !!attemptedToken && currentToken !== attemptedToken
  }
}
